import { useMemo, useState } from 'react';
import { Trash2, Sparkles } from 'lucide-react';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import EmptyState from '../../../components/EmptyState';
import Button from '../../../components/Button';
import type { TriggerLogOption, TriggerLogOptionCategory } from '../services/triggerLogOptionTypes';

interface TriggerLogOptionsListProps {
    options: TriggerLogOption[];
    isLoading: boolean;
    isCurrentUser: boolean;
    onClearAll: () => Promise<void>;
    onGenerate: () => Promise<void>;
}

export const TriggerLogOptionsList = ({ options, isLoading, isCurrentUser, onClearAll, onGenerate }: TriggerLogOptionsListProps) => {
    const { formatDate } = useFormatUtils();
    const [isClearing, setIsClearing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const groupedOptions = useMemo(() => {
        const groups: Record<TriggerLogOptionCategory, TriggerLogOption[]> = {
            obsession: [],
            compulsion: [],
            trigger: [],
        };

        options.forEach((option) => {
            groups[option.category].push(option);
        });

        return groups;
    }, [options]);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            await onGenerate();
        } catch (error) {
            console.error('Error generating options:', error);
            alert('Failed to generate options. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-gray-600">Loading quick options...</div>
            </div>
        );
    }

    if (options.length === 0) {
        return (
            <div className="space-y-4">
                {isCurrentUser && (
                    <div className="flex justify-end mb-4">
                        <Button
                            onClick={handleGenerate}
                            variant="primary"
                            icon={<Sparkles className="w-4 h-4" />}
                            disabled={isGenerating}
                        >
                            {isGenerating ? 'Generating...' : 'Generate Options'}
                        </Button>
                    </div>
                )}
                <EmptyState title="No quick options" description="No quick options found for this user" />
            </div>
        );
    }

    const handleClearAll = async () => {
        const confirmed = window.confirm(
            `Are you sure you want to delete all ${options.length} quick options? This action cannot be undone.`
        );

        if (!confirmed) {
            return;
        }

        setIsClearing(true);
        try {
            await onClearAll();
        } catch (error) {
            console.error('Error clearing options:', error);
            alert('Failed to clear options. Please try again.');
        } finally {
            setIsClearing(false);
        }
    };

    const renderCategory = (category: TriggerLogOptionCategory, categoryOptions: TriggerLogOption[]) => {
        const categoryLabels: Record<TriggerLogOptionCategory, string> = {
            obsession: 'Obsessions',
            compulsion: 'Compulsions',
            trigger: 'Triggers',
        };

        const categoryColors: Record<TriggerLogOptionCategory, string> = {
            obsession: 'border-purple-500',
            compulsion: 'border-blue-500',
            trigger: 'border-orange-500',
        };

        return (
            <div key={category} className="mb-8">
                <h3 className={`text-lg font-semibold text-gray-900 mb-4 border-l-4 pl-3 ${categoryColors[category]}`}>
                    {categoryLabels[category]} ({categoryOptions.length})
                </h3>
                {categoryOptions.length === 0 ? (
                    <p className="text-sm text-gray-500 italic ml-4">No options in this category</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryOptions.map((option) => (
                            <div
                                key={option.id}
                                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start gap-3 mb-2">
                                    <span className="text-2xl">{option.emoji}</span>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                                            {option.shortLabel}
                                        </h4>
                                        <p className="text-xs text-gray-600 mt-1">
                                            {option.longLabel}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>Used: {option.usageCount} times</span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-400">
                                        Last used: {option.lastUsedAt ? formatDate(option.lastUsedAt.getTime()) : 'Never'}
                                    </div>
                                    <div className="mt-1 text-xs text-gray-400">
                                        Created: {formatDate(option.createdAt.getTime())}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                    Quick Options ({options.length} total)
                </h2>
                {isCurrentUser && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            onClick={handleGenerate}
                            variant="primary"
                            icon={<Sparkles className="w-4 h-4" />}
                            disabled={isGenerating}
                        >
                            {isGenerating ? 'Generating...' : 'Generate Options'}
                        </Button>
                        {options.length > 0 && (
                            <Button
                                onClick={handleClearAll}
                                variant="danger"
                                icon={<Trash2 className="w-4 h-4" />}
                                disabled={isClearing}
                            >
                                {isClearing ? 'Clearing...' : 'Clear All'}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {renderCategory('obsession', groupedOptions.obsession)}
            {renderCategory('compulsion', groupedOptions.compulsion)}
            {renderCategory('trigger', groupedOptions.trigger)}
        </div>
    );
};
